import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getDefaultRoleLabel } from "@/contexts/RoleLabelsContext";
import { Building2, Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { toast } from "@/hooks/use-toast";

interface UserCompany {
  companyId: number;
  companyName: string;
  companySlug: string;
  role: string;
}

export default function AccountSwitcher() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery<{
    companies: UserCompany[];
    selectedCompanyId: number | null;
  }>({
    queryKey: ["/api/user-companies"],
  });

  const companies = data?.companies;

  const selectCompany = useMutation({
    mutationFn: async (companyId: number) => {
      return await apiRequest("POST", "/api/select-company", { companyId });
    },
    onSuccess: () => {
      // Invalidate all queries to refresh with new company context
      queryClient.invalidateQueries();

      // Show success message
      toast({
        title: "Company switched",
        description: "You're now viewing the selected company",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to switch company",
        variant: "destructive",
      });
    },
  });

  // Don't show if loading or user belongs to only one company
  if (isLoading || !companies || !Array.isArray(companies) || companies.length <= 1) {
    return null;
  }

  // Find the current company
  const currentCompany = companies.find((c) => c.companyId === user?.companyId);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-between text-left font-normal"
          data-testid="button-account-switcher"
        >
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-gray-500" />
            <span className="text-sm truncate max-w-[150px]">
              {currentCompany?.companyName || "Select company"}
            </span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 text-gray-500" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-xs text-gray-500">Switch Company</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {companies.map((company) => (
          <DropdownMenuItem
            key={company.companyId}
            onClick={() => selectCompany.mutate(company.companyId)}
            disabled={selectCompany.isPending || company.companyId === user?.companyId}
            className="cursor-pointer"
            data-testid={`menu-item-company-${company.companyId}`}
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <div>
                  <p className="font-medium">{company.companyName}</p>
                  <p className="text-xs text-gray-500">{getDefaultRoleLabel(company.role)}</p>
                </div>
              </div>
              {company.companyId === user?.companyId && <Check className="h-4 w-4 text-primary" />}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
